import { useEffect, useMemo, useRef, useState } from "react";
import GridLayout from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import "./grid-overrides.css";
import ChartCard from "./ChartCard";

/** Width-tracking wrapper since react-grid-layout v2 dropped WidthProvider export. */
function useContainerWidth() {
  const ref = useRef(null);
  const [w, setW] = useState(1024);
  useEffect(() => {
    if (!ref.current) return;
    const el = ref.current;
    const update = () => setW(el.clientWidth || 1024);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener("resize", update);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", update);
    };
  }, []);
  return [ref, w];
}

/**
 * Drag/resize chart layout backed by react-grid-layout.
 * - 12 column grid; each chart is a grid item with width/height read from chart.width/chart.height (rows of 60px)
 * - Persists layout via PATCH /api/charts/{id} on drop/resize, debounced
 */
export default function ChartGrid({ charts, canEdit, isAdmin = false, onEdit, onDelete, onAddAlarm, onShowEvents, onLayoutChange, onPersistChart, onChartUpdate, range, refreshKey }) {
  const [layout, setLayout] = useState([]);
  const debounceRef = useRef({});

  useEffect(() => {
    setLayout(
      charts.map((c, i) => ({
        i: c.id,
        x: (i * 6) % 12,
        y: Infinity, // append
        w: Math.max(3, Math.min(12, c.width || 6)),
        h: Math.max(3, Math.min(12, Math.round((c.height || 280) / 60))),
        minW: 3,
        minH: 3,
        maxW: 12,
      }))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [charts.map((c) => c.id).join("|")]);

  function persistItem(item) {
    const chart = charts.find((c) => c.id === item.i);
    if (!chart) return;
    const nextW = item.w;
    const nextH = item.h * 60;
    if (chart.width === nextW && chart.height === nextH) return;
    clearTimeout(debounceRef.current[item.i]);
    debounceRef.current[item.i] = setTimeout(() => {
      onPersistChart?.(item.i, { width: nextW, height: nextH });
    }, 350);
  }

  function handleLayoutChange(newLayout) {
    setLayout(newLayout);
    onLayoutChange?.(newLayout);
  }

  function handleResize(_l, _old, item) {
    persistItem(item);
  }
  function handleDragStop(_l, _old, item) {
    onLayoutChange?.(_l);
  }

  const chartsById = useMemo(() => Object.fromEntries(charts.map((c) => [c.id, c])), [charts]);

  const [containerRef, width] = useContainerWidth();

  return (
    <div ref={containerRef}>
      <GridLayout
        className="layout"
        layout={layout}
        cols={12}
        rowHeight={60}
        width={width}
        margin={[16, 16]}
        isDraggable={canEdit}
        isResizable={canEdit}
        onLayoutChange={handleLayoutChange}
        onResizeStop={handleResize}
        onDragStop={handleDragStop}
        draggableHandle=".chart-drag-handle"
        compactType="vertical"
      >
        {charts.map((c) => (
          <div key={c.id} className="rgl-cell">
            <ChartCard
              chart={chartsById[c.id]}
              canEdit={canEdit}
              canConfigure={isAdmin}
              onEdit={onEdit}
              onDelete={onDelete}
              onAddAlarm={onAddAlarm}
              onShowEvents={onShowEvents}
              onChartUpdate={onChartUpdate}
              range={range}
              refreshKey={refreshKey}
              inGrid
            />
          </div>
        ))}
      </GridLayout>
    </div>
  );
}
